import type { Campaign, LeadCard } from "@/lib/types";

/**
 * A contact the record already has, put on a campaign's dial list by hand (ADR-125 in `platform`).
 *
 * core-api decides: `POST /leads/{id}/campaigns` refuses with a `code`. What is here keeps the
 * refusals the card already shows off the button (do-not-call, SMS only). Consent is not one of
 * them: every contact has it, and one made before that gets it as it joins (platform ADR-194).
 */

/** Why the card alone says this contact joins no dial list, before core-api is asked. */
export interface EnlistBlock {
  code: "dnc" | "sms_only";
  text: string;
}

export function enlistBlock(contact: Pick<LeadCard, "dnc" | "contact_channel">): EnlistBlock | null {
  if (contact.dnc) {
    return { code: "dnc", text: "This number is on the do-not-call list, so it joins no dial list." };
  }
  if (contact.contact_channel === "sms_only") {
    return {
      code: "sms_only",
      text: "This contact asked for texts only, so the dialer does not call them. Change the communication preference first.",
    };
  }
  return null;
}

/** The campaigns the contact is on no list of, by name: the ones the dialog offers. */
export function joinableCampaigns<C extends Pick<Campaign, "id" | "name">>(
  campaigns: readonly C[],
  contact: Pick<LeadCard, "campaigns">,
): C[] {
  const joined = new Set(contact.campaigns.map((m) => m.campaign_id));
  return campaigns.filter((c) => !joined.has(c.id)).sort((a, b) => a.name.localeCompare(b.name));
}

/** core-api's `code` on a refusal, when the body carries one. */
export function refusalCode(body: unknown): string | null {
  const code = (body as { code?: unknown } | null | undefined)?.code;
  return typeof code === "string" ? code : null;
}
